<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useUserStore from "../stores/useUserStore.ts";
import Logo from '../components/Logo.vue';
import UserMenu from '../components/UserMenu.vue';
import ImpersonationControl from '../components/ImpersonationControl.vue';
import Avatar from 'primevue/avatar';
import { getLocaleFromPath, addLocaleToPath, removeLocaleFromPath } from '../i18n/index.js';
import { useLandingRedirect } from '../composables/useLandingRedirect.js';

const router = useRouter();
const { t, locale, locales } = useI18n();
const userStore = useUserStore();
const { redirectToHome } = useLandingRedirect();
const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);
const isLanguageDropdownOpen = ref(false);

// Get current locale for navigation
const currentLocale = computed(() => getLocaleFromPath(router.currentRoute.value.path));

const publicNavigationItems = [
  // All public marketing pages have been migrated to seo-ssr
  // Only auth-related pages remain in seo-ui
];

const privateNavigationItems = [
  { name: 'nav_create_content', path: '/app/seo', icon: 'pi pi-file-edit' },
  // { name: 'nav_content_library', path: '/app/seo/created', icon: 'pi pi-folder' }
];

// For desktop view, exclude the private navigation items that should only be in UserMenu
const desktopNavigationItems = computed(() => 
  userStore.isAuthenticated ? [] : publicNavigationItems
);

// For mobile view, include all navigation items
const mobileNavigationItems = computed(() => 
  userStore.isAuthenticated ? privateNavigationItems : publicNavigationItems
);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  if (typeof document !== 'undefined') {
    document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : '';
  }
};

const navigateTo = (path) => {
  // Add current locale to the path for proper navigation
  const localizedPath = addLocaleToPath(path, currentLocale.value);
  router.push(localizedPath);
  isMobileMenuOpen.value = false;
};

// Handle logo click - redirect to seo-ssr home if logged out, or to app if logged in
const handleLogoClick = () => {
  if (userStore.isAuthenticated) {
    // Let router guard handle navigation based on subscription status
    const homePath = addLocaleToPath('/', currentLocale.value);
    router.push(homePath);
  } else {
    // Redirect to seo-ssr home for unauthenticated users
    redirectToHome();
  }
};

const handleLogin = () => {
  navigateTo('/login');
  isMobileMenuOpen.value = false;
};

const handleGetStarted = () => {
  navigateTo('/register');
  isMobileMenuOpen.value = false;
};

const switchLanguage = async (newLocale) => {
  // Get the current route path
  const currentPath = router.currentRoute.value.path;
  
  // Remove any existing locale prefix
  const pathWithoutLocale = removeLocaleFromPath(currentPath);
  
  // Navigate to the same page with the new locale - router guard will set locale from URL
  if (newLocale === 'en') {
    // For English (default), don't add any prefix
    router.push(pathWithoutLocale || '/');
  } else {
    // For other languages, add the locale prefix
    router.push(`/${newLocale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`);
  }
  
  // Close dropdown
  isLanguageDropdownOpen.value = false;
};

const toggleLanguageDropdown = () => {
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value;
};

const handleClickOutside = (event) => {
  const languageSwitcher = event.target.closest('.language-switcher');
  if (!languageSwitcher) {
    isLanguageDropdownOpen.value = false;
  }
};

const handleLogout = async () => {
  try {
    isMobileMenuOpen.value = false;
    await userStore.logout();
    // Use locale-aware navigation for logout redirect
    const loginPath = addLocaleToPath('/login', currentLocale.value);
    router.push(loginPath);
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', handleScroll);
    // Close language dropdown when clicking outside
    document.addEventListener('click', handleClickOutside);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>

<template>
  <header 
    class="header" 
    :class="{ 
      'header--scrolled': isScrolled,
      'header--menu-open': isMobileMenuOpen
    }"
  >
    <div class="container-header flex justify-between items-center h-full">
      <!-- Brand -->
      <div class="brand">
        <a 
          href="/" 
          class="brand-link"
          @click.prevent="handleLogoClick"
        >
          <Logo size="sm" />
        </a>
      </div>

      <!-- Desktop Navigation -->
      <nav class="desktop-nav">
        <ul>
          <li v-for="item in desktopNavigationItems" :key="item.name">
            <a
              :href="item.path"
              :class="{ 'active': router.currentRoute.value.path === item.path }"
              @click.prevent="navigateTo(item.path)"
            >
              {{ t(item.name) }}
            </a>
          </li>
        </ul>
      </nav>

      <!-- Desktop Right Section -->
      <div class="desktop-right-section">
        <ImpersonationControl />

        <!-- Language Switcher -->
        <div class="relative language-switcher-container">
          <div class="language-switcher" @click="toggleLanguageDropdown">
            <span class="language-text">{{ locale.toUpperCase() }}</span>
            <i class="pi pi-chevron-down language-arrow"></i>
          </div>
          
          <!-- Language Dropdown -->
          <div 
            v-show="isLanguageDropdownOpen"
            class="language-dropdown"
          >
            <button
              @click="switchLanguage('en')"
              class="language-option"
              :class="{ 'language-option-active': locale === 'en' }"
            >
              English
            </button>
            <button
              @click="switchLanguage('es')"
              class="language-option"
              :class="{ 'language-option-active': locale === 'es' }"
            >
              Español
            </button>
            <button
              @click="switchLanguage('ca')"
              class="language-option"
              :class="{ 'language-option-active': locale === 'ca' }"
            >
              Català
            </button>
          </div>
        </div>
        
        <template v-if="!userStore.isAuthenticated">
          <button
            class="header-btn"
            @click="handleLogin"
          >
            {{ t('nav_login') }}
          </button>
          <button
            class="header-btn primary"
            @click="handleGetStarted"
          >
            {{ t('nav_get_started') }}
          </button>
        </template>
        
        <template v-else-if="userStore.isAuthenticated">
          <UserMenu />
        </template>
      </div>

      <!-- Mobile Right Section -->
      <div class="mobile-right-section">
        <!-- Language Switcher for Mobile -->
        <div class="relative language-switcher-container">
          <div class="language-switcher" @click="toggleLanguageDropdown">
            <span class="language-text">{{ locale.toUpperCase() }}</span>
            <i class="pi pi-chevron-down language-arrow"></i>
          </div>
          
          <!-- Mobile Language Dropdown -->
          <div 
            v-show="isLanguageDropdownOpen"
            class="language-dropdown"
          >
            <button
              @click="switchLanguage('en')"
              class="language-option"
              :class="{ 'language-option-active': locale === 'en' }"
            >
              English
            </button>
            <button
              @click="switchLanguage('es')"
              class="language-option"
              :class="{ 'language-option-active': locale === 'es' }"
            >
              Español
            </button>
            <button
              @click="switchLanguage('ca')"
              class="language-option"
              :class="{ 'language-option-active': locale === 'ca' }"
            >
              Català
            </button>
          </div>
        </div>
        
        <!-- Mobile menu button -->
        <button class="mobile-menu-button" @click="toggleMobileMenu">
          <i class="pi pi-bars"></i>
        </button>
      </div>
    </div>
  </header>

  <!-- Mobile menu overlay -->
  <div
    v-if="isMobileMenuOpen"
    class="mobile-menu-overlay"
    @click="isMobileMenuOpen = false"
  ></div>

  <!-- Mobile menu -->
  <div
    class="mobile-menu"
    :class="{ 'mobile-menu-open': isMobileMenuOpen }"
  >
    <div class="mobile-menu-content">
      <!-- User information section for authenticated users -->
      <div v-if="userStore.isAuthenticated" class="flex items-center mb-4 p-3 bg-gray-50 rounded-lg">
        <Avatar 
          :image="userStore.userProfile?.photoURL" 
          :label="userStore.userInitial"
          shape="circle" 
          size="large"
          class="mobile-user-avatar" 
        />
        <div class="ml-3">
          <div class="text-base font-medium text-gray-800">{{ userStore.userProfile?.name || t('nav_guest') }}</div>
          <div class="text-sm text-gray-500">{{ userStore.userProfile?.email }}</div>
        </div>
      </div>

      <div class="mobile-nav-items">
        <template v-for="item in mobileNavigationItems" :key="item.name">
          <a
            :href="item.path"
            class="mobile-nav-link"
            :class="{ 'active': router.currentRoute.value.path === item.path }"
            @click.prevent="navigateTo(item.path)"
          >
            {{ t(item.name) }}
          </a>
        </template>
      </div>
      
      <!-- User profile section for authenticated users -->
      <div v-if="userStore.isAuthenticated" class="mt-6">
        <div class="px-1 pt-2 pb-1">
          <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">{{ t('nav_profile_section') }}</h3>
          
          <a 
            href="/profile/management"
            class="mobile-nav-link flex items-center px-4 py-3 rounded-md text-base font-medium"
            @click.prevent="navigateTo('/profile/management')"
          >
            <i class="pi pi-user mr-3 text-lg"></i>
            {{ t('nav_account') }}
          </a>
          
          <a 
            href="/profile/subscription"
            class="mobile-nav-link flex items-center px-4 py-3 rounded-md text-base font-medium"
            @click.prevent="navigateTo('/profile/subscription')"
          >
            <i class="pi pi-credit-card mr-3 text-lg"></i>
            {{ t('nav_subscription') }}
          </a>
          
          <div class="mobile-menu-divider my-3"></div>
          
          <button 
            class="mobile-nav-link flex items-center w-full text-left px-4 py-3 rounded-md text-base font-medium text-red-600"
            @click="handleLogout"
          >
            <i class="pi pi-power-off mr-3 text-lg"></i>
            {{ t('nav_logout') }}
          </button>
        </div>
      </div>
      
      <!-- Mobile CTA buttons -->
      <div v-if="!userStore.isAuthenticated" class="mobile-cta-buttons">
        <button
          class="header-btn w-full"
          @click="handleLogin"
        >
          {{ t('nav_login') }}
        </button>
        <button
          class="header-btn primary w-full"
          @click="handleGetStarted"
        >
          {{ t('nav_get_started') }}
        </button>
      </div>
    </div>
  </div>
  
  <!-- Spacer to prevent content from hiding behind fixed header -->
  <div class="header-spacer"></div>
</template>

<style scoped>
/* Header specific padding override - add more horizontal padding for mobile */
.container-header {
  max-width: var(--container-max-width-default);
  margin: 0 auto;
  /* Mobile devices - add more horizontal padding */
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

@media (min-width: 640px) {
  .container-header {
    /* Reset to default container padding for larger screens */
    padding-left: var(--container-padding-tablet, 2rem);
    padding-right: var(--container-padding-tablet, 2rem);
  }
}

@media (min-width: 1024px) {
  .container-header {
    /* Reset to default container padding for desktop */
    padding-left: var(--container-padding-desktop, 2.5rem);
    padding-right: var(--container-padding-desktop, 2.5rem);
    /* Add max-width constraint for desktop and above */
    max-width: var(--container-max-width-default);
    margin: 0 auto;
  }
}

@media (min-width: 1280px) {
  .container-header {
    /* Reset to default container padding for large screens */
    padding-left: var(--container-padding-large, 3rem);
    padding-right: var(--container-padding-large, 3rem);
  }
}

/* Glass morphism header - aligned with geo-ssr */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  width: 100%;
  height: 72px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header--scrolled {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(24px) saturate(200%);
  -webkit-backdrop-filter: blur(24px) saturate(200%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-bottom-color: rgba(226, 232, 240, 0.6);
}

.header--menu-open {
  background: rgba(255, 255, 255, 0.95);
}

.brand {
  height: 100%;
  display: flex;
  align-items: center;
}

.brand-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.brand-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

/* Let `Logo` component control its own size via its `size` prop */

.desktop-nav ul {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  gap: 5px;
}

.desktop-nav ul a {
  display: block;
  padding: 8px 16px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  text-decoration: none;
  border-radius: 9999px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
}

.desktop-nav ul a:hover {
  background: rgba(255, 255, 255, 0.5);
  color: #475569;
}

.desktop-nav ul a.active {
  background: #ffffff;
  color: #0f172a;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.nav-contact-button {
  display: block;
  padding: 10px 15px 1px;
  color: #6b7280; /* Match footer and navigation text color */
  text-decoration: none;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: inherit;
  text-align: left;
}

.nav-contact-button:hover {
  color: #3b82f6; /* Match footer hover color - primary-600 equivalent */
}

/* Desktop and Mobile Right Sections */
.desktop-right-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mobile-right-section {
  display: none;
  align-items: center;
  gap: 0.75rem;
}

/* Language Switcher Styles - Glass UI aligned with geo-ssr */
.language-switcher-container {
  position: relative;
  z-index: 10001;
}

.language-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(241, 245, 249, 0.6);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Space Grotesk', sans-serif;
}

.language-switcher:hover {
  background: rgba(255, 255, 255, 0.8);
  border-color: #cbd5e1;
}

.language-text {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.language-arrow {
  margin-left: 0.25rem;
  font-size: 0.75rem;
  color: #94a3b8;
  transition: transform 0.2s ease;
}

.language-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 10002;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.language-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: #475569;
  text-align: left;
  transition: all 0.15s ease;
}

.language-option:hover {
  background: rgba(241, 245, 249, 0.8);
  color: #0f172a;
}

.language-option-active {
  color: #3b82f6;
  font-weight: 600;
}

.mobile-menu-button {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: rgba(241, 245, 249, 0.8);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 10px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.2rem;
  z-index: 10000;
}

.mobile-menu-button:hover {
  background: rgba(255, 255, 255, 1);
  border-color: #cbd5e1;
}

/* Mobile menu overlay - Glass UI aligned with geo-ssr */
.mobile-menu-overlay {
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9998;
}

/* Mobile menu styles - Glass UI aligned with geo-ssr */
.mobile-menu {
  position: fixed;
  top: 72px;
  left: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  overflow: hidden;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  transform: translateY(-10px) scale(0.98);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.mobile-menu-open {
  transform: translateY(0) scale(1);
  opacity: 1;
  visibility: visible;
}

.mobile-menu-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.mobile-nav-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;
}

.mobile-nav-link {
  display: block;
  padding: 14px 16px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #475569;
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.mobile-nav-link:hover,
.mobile-nav-link.active {
  background: rgba(241, 245, 249, 0.8);
  color: #334155;
}

.mobile-nav-link.active {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  font-weight: 600;
}

.mobile-nav-link.nav-contact-button {
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  font-size: inherit;
}

.mobile-nav-link.nav-contact-button:hover {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6; /* Match footer hover color - primary-600 equivalent */
}

/* Header now uses global container system */

/* Show mobile menu button and hide desktop nav on smaller screens */
@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }
  
  .desktop-right-section {
    display: none;
  }
  
  .mobile-right-section {
    display: flex;
  }
  
  .mobile-menu-button {
    display: block;
  }
}

@media (min-width: 769px) {
  .mobile-menu {
    display: none;
  }
  
  .mobile-right-section {
    display: none;
  }
}

/* Header Buttons - Glass UI Style aligned with geo-ssr */
.header-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 10px 20px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  white-space: nowrap;
  text-decoration: none;
  outline: none;
}

.header-btn:not(.primary) {
  background: transparent;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.header-btn:not(.primary):hover {
  border-color: #cbd5e1;
  background: rgba(241, 245, 249, 0.5);
  color: #334155;
}

.header-btn.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.header-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.header-btn.primary:active {
  transform: translateY(0);
}

/* Mobile CTA buttons */
.mobile-cta-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  margin-top: 24px;
}

.mobile-cta-buttons .header-btn {
  width: 100%;
  padding: 14px 24px;
  font-size: 16px;
}

.mobile-user-avatar {
  width: 3rem;
  height: 3rem;
}

.mobile-menu-divider {
  height: 1px;
  background-color: #e5e7eb;
}

/* Header spacer for fixed header */
.header-spacer {
  height: 72px;
}

@media (max-width: 1023px) {
  .header {
    height: 64px;
  }
  
  .header-spacer {
    height: 64px;
  }
  
  .container-header {
    padding-left: 16px;
    padding-right: 16px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .header,
  .header-btn,
  .language-switcher,
  .mobile-menu-button,
  .mobile-nav-link {
    transition: none;
  }
}

/* Focus states */
.header-btn:focus-visible,
.language-switcher:focus-visible,
.mobile-menu-button:focus-visible,
.mobile-nav-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .header {
    background: rgba(255, 255, 255, 0.98);
    border-bottom: 2px solid #000;
  }
}
</style>
